<template>
  <div>
    <assign-view
      :visible.sync="visibleAssignModal"
      @assign="fetchFacilities"
      :initial-values="{ associate_ids: [associate.id] }"
    />

    <div class="flex flex-column gap-4">
      <div class="flex justify-content-between align-items-center">
        <div class="flex gap-2">
          <a-button icon="export" type="primary" @click="visibleAssignModal = true">Assign To Client</a-button>
        </div>
      </div>
      <a-table
        size="small"
        :style="{ whiteSpace: 'pre' }"
        :columns="columns"
        :data-source="facilities"
        :loading="loading"
        :pagination="pagination"
        :rowKey="record => record.project.id"
        :scroll="{ y: 380 }"
        :locale="{
          emptyText: 'No Assigned Facilities'
        }"
        @change="handleTableChange"
      >
        <span slot="status" slot-scope="text, record">
          <a-tag :color="record.status === 'connected' ? '#10b981' : '#ef4444'">{{
            record.status === 'connected' ? 'Connected' : 'Disconnected'
          }}</a-tag>
        </span>
        <span slot="actions" slot-scope="text, record">
          <div class="flex gap-2">
            <a-button
              icon="eye"
              type="primary"
              ghost
              title="Open Profile"
              :disabled="loadingOpen !== null"
              :loading="loadingOpen === `${record.project.id}_${record.associate.associate_number}`"
              @click="handleOpenProfile(record.project.id, record.associate.associate_number)"
            />
          </div>
        </span>
      </a-table>
    </div>
  </div>
</template>

<script>
import ClientsAssign from '@/modules/clients/components/clients-assign.vue'
import { Button, notification, Table, Tag } from 'ant-design-vue'
import { createNamespacedHelpers, mapActions } from 'vuex'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')

export default {
  components: {
    'a-table': Table,
    'a-button': Button,
    'a-tag': Tag,
    'assign-view': ClientsAssign
  },
  data() {
    return {
      pagination: {
        showSizeChanger: true,
        pageSizeOptions: ['5', '10', '20', '50'],
        pageSize: 50,
        total: 0,
        current: 1
      },

      visibleAssignModal: false,

      facilities: [],

      columns: [
        {
          title: 'Name',
          dataIndex: 'project.name',
          key: 'project.name',
          width: '300px',
          sorter: (a, b) =>
            a.project.name == null ? -1 : b.project.name == null ? 1 : a.project.name.localeCompare(b.project.name)
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 150,
          scopedSlots: { customRender: 'status' },
          sorter: (a, b) => (a.status == null ? -1 : b.status == null ? 1 : a.status.localeCompare(b.status))
        },
        {
          title: 'Actions',
          dataIndex: 'actions',
          key: 'actions',

          width: 300,
          scopedSlots: { customRender: 'actions' }
        }
      ],

      loading: false,

      loadingOpen: null
    }
  },
  watch: {
    associateFacilities: {
      handler(newVal) {
        this.pagination.total = newVal?.total_count ?? 0
        this.facilities = newVal?.items ?? []
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate,
      associateFacilities: state => state.associateFacilities
    }),

    allAssociateProjects() {
      return this.$store.state.applicationState.allProjects
    }
  },
  mounted() {
    this.fetchFacilities()
  },
  methods: {
    ...mapActions(['switchProject', 'loadApplicationState']),
    ...profileActions(['getAssociateFacilities']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    hasProject(id) {
      return this.allAssociateProjects.find(item => item.id === id)
    },

    async handleOpenProfile(project_id, associate_number) {
      this.loadingOpen = `${project_id}_${associate_number}`

      if (this.hasProject(project_id)) {
        try {
          await this.switchProject({ project_id })

          this.$emit('open', associate_number)
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
        } finally {
          this.loadingOpen = null
        }
      } else {
        this.showNotification(
          'info',
          'Information',
          'You don’t have permission to manage this company! You need to assign yourself as a manager for this company first.'
        )
        this.loadingOpen = null
      }
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }

      this.fetchFacilities()
    },

    async fetchFacilities() {
      this.loading = true

      const payload = {
        associate_id: this.associate.id,
        page: this.pagination.current,
        size: this.pagination.pageSize
      }

      try {
        await this.getAssociateFacilities(payload)
      } catch (error) {
        this.showNotification('error', 'Error', error || 'An error occurred')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
